/**
 * FlowStatus — the status pill that puts words on the v2 triad. Read-only,
 * not a control. Ported from the OnFlow Design System project
 * (v2/components/core/FlowChip.jsx).
 */
import type { CSSProperties } from 'react';

/** The v2 status triad, plus a neutral "no data yet" state. */
export type TrackState = 'on-track' | 'drifting' | 'off-track' | 'neutral';

const MAP: Record<TrackState, [color: string, wash: string, fallback: string]> = {
  'on-track': ['var(--on-track)', 'var(--on-track-wash)', 'On track'],
  drifting: ['var(--drifting)', 'var(--drifting-wash)', 'Drifting'],
  'off-track': ['var(--off-track)', 'var(--off-track-wash)', 'Off track'],
  neutral: ['var(--ink-2)', 'var(--paper-3)', 'No data'],
};

interface FlowStatusProps {
  state?: TrackState;
  label?: string;
  dot?: boolean;
  style?: CSSProperties;
}

export function FlowStatus({ state = 'on-track', label, dot = true, style }: FlowStatusProps) {
  const [c, bg, fallback] = MAP[state] ?? MAP.neutral;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: `5px 10px 5px ${dot ? '8px' : '10px'}`,
        borderRadius: 'var(--r-full)',
        background: bg,
        border: `1px solid color-mix(in srgb, ${c} 32%, transparent)`,
        color: c,
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-label-size)',
        fontWeight: 500,
        letterSpacing: 'var(--text-label-tracking)',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {dot && (
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: c, flexShrink: 0 }} />
      )}
      {label || fallback}
    </span>
  );
}
